import React, { useRef, useEffect, useState, KeyboardEvent } from "react";
import { Box, SxProps, Theme } from "@mui/material";
import { colors } from "./colors";

const OTP_LENGTH = 6;
const LAST_INDEX = OTP_LENGTH - 1;

const getInputStyles = (
  isFocused: boolean,
  hasError: boolean,
): SxProps<Theme> => ({
  width: "56px",
  height: "56px",
  backgroundColor: colors.input.background,
  borderRadius: "100px",
  border:
    isFocused || hasError
      ? `1.5px solid ${hasError ? colors.error[400] : colors.primary[500]}`
      : "1.5px solid transparent",
  color: colors.neutral.white,
  fontFamily: "Outfit, sans-serif",
  fontSize: "20px",
  fontWeight: 600,
  lineHeight: "30px",
  textAlign: "center",
  outline: "none",
  cursor: "text",
  transition: "border-color 0.2s ease",
  padding: 0,
  boxSizing: "border-box",
  "&:hover": {
    borderColor: !isFocused && !hasError ? colors.primary[500] : undefined,
  },
});

export interface OTPInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  error?: boolean;
  onComplete?: (value: string) => void;
}

const OTPInput: React.FC<OTPInputProps> = ({
  value,
  onChange,
  error = false,
  onComplete,
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [focusedIndex, setFocusedIndex] = useState<number>(0);

  useEffect(() => {
    const firstEmptyIndex = value.findIndex((v) => !v);
    const indexToFocus =
      firstEmptyIndex !== -1 ? firstEmptyIndex : value.length - 1;
    if (indexToFocus < OTP_LENGTH && inputRefs.current[indexToFocus]) {
      inputRefs.current[indexToFocus]?.focus();
      setFocusedIndex(indexToFocus);
    }
  }, [value]);

  const handleChange = (index: number, newValue: string) => {
    if (newValue.length > 1) {
      newValue = newValue.slice(-1);
    }

    if (newValue && !/^\d$/.test(newValue)) {
      return;
    }

    const newValues = [...value];
    newValues[index] = newValue;
    onChange(newValues);

    if (newValue && index < LAST_INDEX) {
      inputRefs.current[index + 1]?.focus();
      setFocusedIndex(index + 1);
    }

    if (newValues.every((v) => v) && newValues.length === OTP_LENGTH) {
      onComplete?.(newValues.join(""));
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setFocusedIndex(index - 1);
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setFocusedIndex(index - 1);
    } else if (e.key === "ArrowRight" && index < LAST_INDEX) {
      inputRefs.current[index + 1]?.focus();
      setFocusedIndex(index + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, OTP_LENGTH);
    const digits = pastedData.split("").filter((char) => /^\d$/.test(char));

    if (digits.length > 0) {
      const newValues = [...value];
      digits.forEach((digit, i) => {
        if (i < OTP_LENGTH) {
          newValues[i] = digit;
        }
      });
      onChange(newValues);

      const nextEmptyIndex = newValues.findIndex(
        (v, i) => !v && i >= digits.length,
      );
      const focusIndex =
        nextEmptyIndex !== -1
          ? nextEmptyIndex
          : Math.min(digits.length, LAST_INDEX);
      inputRefs.current[focusIndex]?.focus();
      setFocusedIndex(focusIndex);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: "16px",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      {Array.from({ length: OTP_LENGTH }).map((_, index) => (
        <Box
          key={index}
          component="input"
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[index] || ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleChange(index, e.target.value)
          }
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
            handleKeyDown(index, e)
          }
          onPaste={handlePaste}
          onFocus={() => setFocusedIndex(index)}
          ref={(el: HTMLInputElement | null) => {
            inputRefs.current[index] = el;
          }}
          sx={getInputStyles(focusedIndex === index, error)}
        />
      ))}
    </Box>
  );
};

export default OTPInput;
